import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { PermissionCode } from "@koclukpanel/authz";
import { cn, Ikon, marka, SayfaBasligi, type IkonAdi } from "@koclukpanel/ui";
import { BakimBandi } from "./BakimBandi";

export interface MenuOgesi {
  anahtar: string;
  etiket: string;
  /**
   * Menu simgesi.
   *
   * ================================================================
   * ZORUNLU — SECMELI DEGIL
   * ================================================================
   * Tip `IkonAdi | undefined` olsaydi yeni bir menu ogesi eklerken
   * atlamak mumkun olurdu ve o oge listede simgesiz, hizasi kaymis
   * bir satir olarak dururdu. Kimse "eksik" demezdi; yalnizca liste
   * biraz daha dagilirdi.
   *
   * Simge METNIN YERINE GECMIYOR: ikisi yan yana duruyor. Ogrenen
   * kullanici simgeyi taniyip hizlaniyor, yeni kullanici metni
   * okuyor. Menu DARALTILINCA metin `sr-only` kaliyor (ekran okuyucu
   * yine okuyor) ve fareyle bakana `title` ipucu olarak gorunuyor.
   */
  ikon: IkonAdi;
  /**
   * Menuyu acan izinlerden EN AZ BIRI yeterli ("su ya da bu").
   *
   * ================================================================
   * NEDEN LISTE
   * ================================================================
   * Tek izinle sinirliyken ayni sinif hata bu projede UC kez cikti:
   * bir ekranin isini iki AYRI rol yapiyor ve ikisi ayni izni
   * tasimiyor.
   *
   *   rapor onayi   : yazan `rapor.veli.olustur`, onaylayan
   *                   `rapor.veli.onayla`
   *   talep kuyrugu : acan `operasyon.talep.olustur`, karara baglayan
   *                   `operasyon.talep.karara_bagla`
   *   tanisma       : yazan `crm.gorusme.rapor`, onaylayan
   *                   `crm.gorusme.onayla`
   *
   * Ilk ikisi SUNUCU kapisinda cikti; ucuncusu tam da menuye
   * eklenirken yakalandi — `crm.gorusme.rapor` yazilsaydi onayci
   * menuyu HIC goremezdi.
   *
   * Menu yalnizca GORSEL kolayliktir; asil kontrol sunucuda.
   */
  /*
    ================================================================
    TİP `string` DEĞİL `PermissionCode` — 05.09.2026
    ================================================================
    `string`ken menüdeki kırk izin dizgisinin hiçbiri korunmuyordu.
    Yazım hatası hata vermiyor, sessizce `false` dönüyor: menü öğesi
    hiç görünmüyor ve kimse sebebini aramıyor.

    Aynı tarama panelde bir tane gerçek hata buldu (`Uygulama.tsx`
    -> `program.hafta.duzenle`, katalogda yok). Menüde çıkmadı ama
    çıkması an meselesiydi: kırk dizge elle yazılmıştı.

    ⚠️ Tip yalnızca "böyle bir izin VAR MI" der. "DOĞRU izin mi"
    sorusunu cevaplamıyor — o ancak ekranın çağırdığı ucun
    dekoratörüyle karşılaştırılarak ölçülür.
  */
  izin?: PermissionCode | readonly PermissionCode[];
  /**
   * Bu girdinin kapsadigi EKRAN anahtarlari (alan modeli, UI/UX Dalga 2).
   * `data-menu` bosluklu liste olarak yaziliyor: karsilama turu bir ekrani
   * vurgularken o ekranin icinde bulundugu alani da bulabilsin.
   */
  ekranlar?: readonly string[];
  /** Icinde durdugu ACILIR grubun anahtari (UNİKOÇ "Öğrencilerim ▾"); yoksa ust duzey girdi. */
  grup?: string;
  /** Ayiricili alt BOLUMUN anahtari (UNİKOÇ "ÖDÜL MERKEZİ"); bolum basligi tiklanmaz. */
  bolum?: string;
}

/** Menudeki bir grup ya da bolum basligi. */
export interface MenuGrubu {
  anahtar: string;
  etiket: string;
  ikon: IkonAdi;
  tur: "acilir" | "bolum";
}

/** Yan menunun ustundeki profil blogu — yalniz GORUNTU (ad + rol adi); hicbir karar buna bakmiyor. */
export interface KabukProfili {
  ad: string;
  rol?: string | undefined;
}

export type MenuDugumu =
  { tur: "oge"; oge: MenuOgesi } | { tur: "grup"; grup: MenuGrubu; ogeler: MenuOgesi[] };

export interface MenuDuzeni {
  /** Ana liste: tek girdiler ve acilir gruplar, menudeki sirasiyla. */
  ana: MenuDugumu[];
  /** Ayiricili alt bolumler (ODUL MERKEZI), ana listenin ALTINDA. */
  bolumler: { bolum: MenuGrubu; ogeler: MenuOgesi[] }[];
}

/**
 * Menuyu UNİKOÇ duzenine dizer (sürüm 9a).
 *
 * Grup, ILK girdisinin yerinde ciziliyor ve grubun butun girdileri onun altina
 * toplanıyor — sira menu verisinden geliyor, ikinci bir sira listesi yok.
 *
 * TANIMI OLMAYAN grup ya da bolum adi DUSMUYOR: anahtarin kendisiyle ciziliyor.
 * Sessizce kaybolan bir menu ogesi, en gec fark edilen hatadir.
 */
export function menuDuzeni(
  menu: readonly MenuOgesi[],
  gruplar: readonly MenuGrubu[] = [],
): MenuDuzeni {
  const tanim = (anahtar: string, tur: MenuGrubu["tur"]): MenuGrubu =>
    gruplar.find((g) => g.anahtar === anahtar) ?? { anahtar, etiket: anahtar, ikon: "bilgi", tur };

  const ana: MenuDugumu[] = [];
  const gorulenGrup = new Map<string, MenuOgesi[]>();
  const bolumler: MenuDuzeni["bolumler"] = [];

  for (const oge of menu) {
    if (oge.bolum) {
      const var_ = bolumler.find((b) => b.bolum.anahtar === oge.bolum);
      if (var_) var_.ogeler.push(oge);
      else bolumler.push({ bolum: tanim(oge.bolum, "bolum"), ogeler: [oge] });
      continue;
    }
    if (oge.grup) {
      const liste = gorulenGrup.get(oge.grup);
      if (liste) {
        liste.push(oge);
      } else {
        const yeni = [oge];
        gorulenGrup.set(oge.grup, yeni);
        ana.push({ tur: "grup", grup: tanim(oge.grup, "acilir"), ogeler: yeni });
      }
      continue;
    }
    ana.push({ tur: "oge", oge });
  }
  return { ana, bolumler };
}

/** Kullanicinin ELLE actigi gruplar (tarayicida, kullanici basina; try/catch ile). */
const ACIK_GRUP_ANAHTARI = "kocluk-panel:menu-acik-gruplar";
/** Masaustunde menu ikon kipine daraltildi mi. */
const DARALTMA_ANAHTARI = "kocluk-panel:menu-daraltilmis";

function acikGruplariOku(): Set<string> {
  try {
    const ham = localStorage.getItem(ACIK_GRUP_ANAHTARI);
    const dizi: unknown = ham ? JSON.parse(ham) : [];
    return new Set(
      Array.isArray(dizi) ? dizi.filter((x): x is string => typeof x === "string") : [],
    );
  } catch {
    return new Set();
  }
}

function acikGruplariYaz(kume: ReadonlySet<string>): void {
  try {
    localStorage.setItem(ACIK_GRUP_ANAHTARI, JSON.stringify([...kume]));
  } catch {
    // Depolama yok/dolu: tercih yalnizca bu oturumda gecerli kalir.
  }
}

function daraltmaOku(): boolean {
  try {
    return localStorage.getItem(DARALTMA_ANAHTARI) === "1";
  } catch {
    return false;
  }
}

function daraltmaYaz(daraltilmis: boolean): void {
  try {
    localStorage.setItem(DARALTMA_ANAHTARI, daraltilmis ? "1" : "0");
  } catch {
    // Depolama yok: daralma yalnizca bu oturumda gecerli.
  }
}

/** "Ad SOYAD" → "AS". Tek kelimeyse ilk harf. */
function basHarfler(ad: string): string {
  const parca = ad.trim().split(/\s+/).filter(Boolean);
  const ilk = parca[0]?.[0] ?? "";
  const son = parca.length > 1 ? (parca[parca.length - 1]?.[0] ?? "") : "";
  return (ilk + son).toLocaleUpperCase("tr-TR");
}

/*
  ================================================================
  SAYFA BASLIGI YUVASI — ekran bandi CIZMIYOR, bandA yaziyor
  ================================================================
  Bant (UNİKOÇ lavanta→pembe baslik karti) kabukta: her ekranda ayni yerde,
  ayni olcude. Ekrana ozgu aciklama ve eylem dugmeleri ise ekranin bilgisi.
  Ekran onlari `SayfaBasligiEki` ile bandin icindeki iki yuvaya PORTAL ediyor.

  Neden portal, neden "ust bilesene state yaz" degil: eylemler her cizimde yeni
  bir ReactNode; onu ust bilesenin state'ine yazmak her cizimde ust bileseni
  yeniden cizdirip sonsuz donguye giriyordu (React'in bilinen tuzagi). Portal
  hicbir state tasimiyor — ekranin kendi agacinin parcasi olarak kaliyor.
*/
interface BaslikYuvalari {
  aciklama: HTMLElement | null;
  eylemler: HTMLElement | null;
}
/*
  Varsayilan DEGER `null` (Provider YOK = Kabuk disinda): ekran birim testinde tek basina ya da
  baska bir kapsayicida cizilirken eylemleri yerinde ciziliyor. Provider VAR ama yuva henuz yok
  (ilk cizim; ref'ler ikinci turda dolar) ya da bant gizli ise bilerek hicbir sey cizilmiyor.
*/
const BaslikYuvasiBaglami = createContext<BaslikYuvalari | null>(null);

export interface SayfaBasligiEkiProps {
  /** Bandin basliginin altindaki gri tek satir. */
  aciklama?: ReactNode;
  /** Bandin sagindaki dugmeler (kesikli hayalet, birincil). */
  eylemler?: ReactNode;
}

/**
 * Ekranin, kabuktaki sayfa basligi bandina aciklama ve eylem eklemesi.
 * Kabugun DISINDA (birim testinde ekran tek basina cizilirken) bant yok: aciklama ve
 * eylemler OLDUGU YERDE cizilir — eylemler sessizce kaybolmasin (onceden hicbir sey cizilmiyordu
 * ve ekranin dugmeleri testte de gorunmez oluyordu). Kabuk icinde ise banda portal edilir; bant
 * gizliyse (`baslikBandiGizli`) yazilacak yuva yoktur ve hicbir sey cizilmez.
 */
export function SayfaBasligiEki({ aciklama, eylemler }: SayfaBasligiEkiProps) {
  const yuva = useContext(BaslikYuvasiBaglami);
  if (yuva === null) {
    if (!aciklama && !eylemler) return null;
    return (
      <div
        data-ui="sayfa-basligi-eki"
        className="mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2"
      >
        {aciklama && <p className="text-metin-ikincil min-w-0 text-sm">{aciklama}</p>}
        {eylemler && (
          <div className="flex max-w-full min-w-0 flex-wrap items-center gap-2">{eylemler}</div>
        )}
      </div>
    );
  }
  return (
    <>
      {aciklama && yuva.aciklama && createPortal(aciklama, yuva.aciklama)}
      {eylemler && yuva.eylemler && createPortal(eylemler, yuva.eylemler)}
    </>
  );
}

interface KabukProps {
  menu: readonly MenuOgesi[];
  /** Grup ve bolum tanimlari (ad, ikon). Girdiler `grup`/`bolum` anahtariyla buraya baglaniyor. */
  gruplar?: readonly MenuGrubu[];
  aktif: string;
  onSec: (anahtar: string) => void;
  baslik: string;
  /** Baslik bandinin sol karosundaki ikon — genelde aktif menu girdisinin ikonu. */
  baslikIkonu?: IkonAdi | undefined;
  /** Yan menu ustundeki profil blogu; yuklenirken `undefined` (yer tutucu cember). */
  profil?: KabukProfili | null | undefined;
  /**
   * "Cikis Yap" — menunun EN ALTINDA (karar 07.10.2026, ONERI §5.4).
   * Verilmezse satir cizilmez.
   */
  onCikis?: () => void;
  /**
   * Sag ustte duran oge — bildirim zili (F-NTF-09).
   *
   * "Her ekranda AYNI YERDE" sartnamenin kendi cumlesi: bildirimi
   * ekran ekran farkli yere koymak, kullanicinin her seferinde
   * aramasi demek. Kabuga verilmesinin sebebi bu — tek yerde durur.
   */
  saginUstu?: ReactNode;
  /**
   * Sayfa basliginin altinda duran alan sekmeleri (UI/UX Dalga 2). Kabuk ne
   * cizdigini bilmiyor; yalnizca yerini veriyor.
   */
  altSekmeler?: ReactNode;
  /**
   * Baslik bandi CIZILMESIN (UNİKOÇ Anasayfa'sinda bant yok, tarama-1 §3.1). Sayfanin `h1`i
   * yine de duruyor, yalniz gorunmuyor (`sr-only`): ekran okuyucu ve sayfa basligi gezintisi
   * icin sayfa basliksiz kalmamali. `SayfaBasligiEki` bu durumda yazacak yuva bulamaz ve
   * hicbir sey cizmez.
   */
  baslikBandiGizli?: boolean;
  /**
   * Secili ogrencinin adi. Baslige `Ad — Ekran` diye YAPISTIRILMIYOR (menudeki etiket ile baslik
   * ayni kalsin, ogrenci adi her ekranda baslikta tekrar etmesin): bandin aciklama satirinda hap olarak duruyor.
   */
  ogrenciAdi?: string | undefined;
  children: ReactNode;
}

/**
 * PANEL KABUGU — UNİKOÇ gorunumu (sürüm 9a, Emre karari 07.10.2026)
 *
 * Mor degrade yan menu (260 px; masaustunde hamburgerle 83 px ikon kipi),
 * ustte hamburger + marka, ortada profil blogu, acilir gruplar, en altta
 * "ÖDÜL MERKEZİ" bolumu ve Cikis Yap. Icerik lavanta→pembe zeminde, her
 * ekranin basinda ayni baslik bandi. Olculer ve renkler
 * eski-tasarim/tarama-1.md §1–§2'den.
 *
 * Kenar menu mobilde CEKMECE, masaustunde sabit. Onceki iskelette sabit
 * 240px genislikteydi ve hicbir kirilim yoktu — 375px telefonda ana
 * icerige ~135px kaliyordu (denetim bulgusu 5.6).
 */
export function Kabuk({
  menu,
  gruplar,
  aktif,
  onSec,
  baslik,
  baslikIkonu,
  profil,
  onCikis,
  saginUstu,
  altSekmeler,
  baslikBandiGizli = false,
  ogrenciAdi,
  children,
}: KabukProps) {
  const [acik, setAcik] = useState(false);
  const [acikGruplar, setAcikGruplar] = useState<Set<string>>(acikGruplariOku);
  const [daraltilmisTercih, setDaraltilmisTercih] = useState<boolean>(daraltmaOku);
  /** Aktif sayfanin grubunu kullanici ELLE kapattiysa — sayfa degisince sifirlaniyor. */
  const [kapatilanAktifGrup, setKapatilanAktifGrup] = useState<string | null>(null);
  const [aciklamaYuvasi, setAciklamaYuvasi] = useState<HTMLElement | null>(null);
  const [eylemYuvasi, setEylemYuvasi] = useState<HTMLElement | null>(null);
  const cekmeceRef = useRef<HTMLElement>(null);
  const menuDugmesiRef = useRef<HTMLButtonElement>(null);
  const masaustu = useMasaustu();
  const acilmistiRef = useRef(false);

  /*
    ================================================================
    CEKMECE ODAK YASAM DONGUSU — UI/UX Dalga 1 (mutabakat F01, M-04)
    ================================================================
    Olculdu (05.10.2026, 390 px): kapali cekmece yalnizca ekran disina
    KAYDIRILIYORDU; Tab sirasinin ilk 15 duraginin 12'si gorunmeyen menu
    ogeleriydi ve kapaninca odak hicbir yere donmuyordu.
    - Kapaliyken (telefonda) `inert`: odak ve erisilebilirlik agacindan cikar.
    - Acikken odak ICERIDE: Tab son ogeden ilkine, Shift+Tab ilkinden sonuncuya.
    - Kapaninca (Escape, arka plan, menu secimi) odak acan ☰ dugmesine doner.
    Masaustunde cekmece degil sabit kenar cubugu: hicbiri uygulanmaz.
  */
  useEffect(() => {
    if (!acik) {
      if (acilmistiRef.current) menuDugmesiRef.current?.focus();
      acilmistiRef.current = false;
      return;
    }
    acilmistiRef.current = true;
    cekmeceRef.current?.focus();
    const tus = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setAcik(false);
        return;
      }
      if (e.key !== "Tab" || !cekmeceRef.current) return;
      const odaklanabilir = [
        ...cekmeceRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        ),
      ];
      const ilk = odaklanabilir[0];
      const son = odaklanabilir[odaklanabilir.length - 1];
      if (!ilk || !son) return;
      const etkin = document.activeElement;
      if (e.shiftKey && (etkin === ilk || etkin === cekmeceRef.current)) {
        e.preventDefault();
        son.focus();
      } else if (!e.shiftKey && etkin === son) {
        e.preventDefault();
        ilk.focus();
      }
    };
    document.addEventListener("keydown", tus);
    return () => document.removeEventListener("keydown", tus);
  }, [acik]);

  const duzen = menuDuzeni(menu, gruplar);
  /** Aktif girdinin grubu — o grup kendiliginden ACIK (UNİKOÇ: "aktif sayfa grubun icindeyse grup acik gelir"). */
  const aktifGrup = menu.find((o) => o.anahtar === aktif)?.grup ?? null;

  // Sayfa degisince "aktif grubu elle kapattim" bilgisi duser: yeni sayfanin grubu yine acik gelir.
  useEffect(() => {
    setKapatilanAktifGrup(null);
  }, [aktif]);

  /*
    Karsilama turu aciksa menu daraltilmis sayilmaz (vurgulanan girdinin grubu da acilir, bkz. `grupAcikMi`) —
    turun vurguladigi girdi gizli kalirsa tur anlamsiz olur.
  */
  const turAktif = typeof document !== "undefined" && document.body.hasAttribute("data-tur-aktif");
  // Daralma YALNIZ masaustunde: telefonda cekmece zaten tam genislikte aciliyor.
  const daraltilmis = masaustu && daraltilmisTercih && !turAktif;

  /*
    Tur sirasinda YALNIZ vurgulanan girdinin grubu acilir (surum 9a, Emre karari 07.10.2026: menu gruplari KAPALI
    baslar, UNİKOÇ gibi). Eskiden tur boyunca BUTUN gruplar aciliyordu: ilk giriste tur calisan kullanici menuyu
    tamamen acik goruyordu. Vurgulanan girdi gizli kalmasin diye yalniz onun grubu aciliyor.
  */
  const turAnahtari =
    typeof document !== "undefined" ? document.body.getAttribute("data-tur-aktif") : null;
  const turGrubu = turAnahtari
    ? (menu.find((o) => (o.ekranlar ?? [o.anahtar]).includes(turAnahtari))?.grup ?? null)
    : null;

  const grupAcikMi = (grup: string): boolean =>
    grup === turGrubu ||
    acikGruplar.has(grup) ||
    (grup === aktifGrup && kapatilanAktifGrup !== grup);

  const grubuDegistir = (grup: string): void => {
    if (daraltilmis) {
      // Ikon kipinde grup basligina basmak: menu genisler ve grup acilir — alt girdiler ancak orada gorunur.
      setDaraltilmisTercih(false);
      daraltmaYaz(false);
      if (!grupAcikMi(grup)) grubuAyarla(grup, true);
      return;
    }
    grubuAyarla(grup, !grupAcikMi(grup));
  };
  const grubuAyarla = (grup: string, ac: boolean): void => {
    setAcikGruplar((onceki) => {
      const yeni = new Set(onceki);
      if (ac) yeni.add(grup);
      else yeni.delete(grup);
      acikGruplariYaz(yeni);
      return yeni;
    });
    if (grup === aktifGrup) setKapatilanAktifGrup(ac ? null : grup);
  };

  const hamburger = (): void => {
    if (!masaustu) {
      setAcik(false);
      return;
    }
    setDaraltilmisTercih((d) => {
      daraltmaYaz(!d);
      return !d;
    });
  };

  const secildi = (anahtar: string): void => {
    onSec(anahtar);
    setAcik(false);
  };

  const satir = (oge: MenuOgesi, alt: boolean): ReactNode => (
    <li key={oge.anahtar} data-menu={(oge.ekranlar ?? [oge.anahtar]).join(" ")}>
      <MenuSatiri
        ikon={oge.ikon}
        etiket={oge.etiket}
        alt={alt}
        secili={oge.anahtar === aktif}
        daraltilmis={daraltilmis}
        onClick={() => secildi(oge.anahtar)}
      />
    </li>
  );

  return (
    /*
      ================================================================
      `panel-yuzeyi` — PANEL GORUNUMUNUN KAPSAMI
      ================================================================
      Sürüm 9a (07.10.2026): panel UNİKOÇ'a birebir. Renk/kose/golge/yazi tipi
      degerleri `stil.css` icinde bu sinifin ALTINDA yeniden tanimli; GIRIS
      EKRANI (`GirisKabugu`) ve ogrenci uygulamasi bu kapsamin disinda kaldigi
      icin eski degerlerle cizilmeye devam ediyor (karar: giris sayfasina
      dokunulmaz).
    */
    <div className="panel-yuzeyi text-notr-900 min-h-screen bg-(image:--gradyan-unk-zemin) bg-fixed">
      {/*
        BAKIM BANDI EN USTTE — F-SEC-06. Kimlikli BUTUN ekranlarda gorunsun diye kabukta; giris ekraninin
        kendi kopyasi `Giris.tsx`te. Engelleme sunucuda (503); bant yalnizca nedenini soyluyor.
      */}
      <BakimBandi />

      <div
        className={cn(
          "md:flex",
          // Menu `fixed` oldugu icin akistan cikti: genisligi kadar yer ayriliyor (menuyle ayni gecis).
          "ease-cikis transition-[padding] duration-(--sure-normal)",
          daraltilmis ? "md:pl-[83px]" : "md:pl-[260px]",
        )}
      >
        {/* Klavye kullanicisi menuyu her sayfada bastan gecmesin */}
        <a
          href="#icerik"
          className="focus:rounded-alan focus:bg-notr-0 sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:px-4 focus:py-2 focus:shadow"
        >
          İçeriğe geç
        </a>

        {/* Mobil ust cubuk — menu rengiyle: telefonda tek mor yuzey burasi, cekmece kapaliyken. */}
        <header className="text-menu-metin flex items-center gap-2 bg-(image:--gradyan-unk-menu) px-2 py-2 md:hidden">
          <button
            ref={menuDugmesiRef}
            type="button"
            onClick={() => setAcik(true)}
            aria-expanded={acik}
            aria-controls="kenar-menu"
            className="size-dokunma bg-menu-aktif ring-menu-halka hover:bg-menu-uzeri inline-grid place-items-center rounded-[0.625rem] ring-1 ring-inset"
          >
            <span className="sr-only">Menüyü aç</span>
            <Ikon ad="menu" className="size-5" />
          </button>
          <span className="truncate text-lg font-extrabold tracking-[0.02em] uppercase">
            {marka.kisaAd}
          </span>
          {/* Telefonda da SAG UST — masaustuyle ayni yer. */}
          {saginUstu && <div className="ml-auto">{saginUstu}</div>}
        </header>

        {/* Cekmece arka plani */}
        {acik && (
          <div
            className="bg-notr-900/40 fixed inset-0 z-30 backdrop-blur-[1px] md:hidden"
            onClick={() => setAcik(false)}
            aria-hidden="true"
          />
        )}

        <nav
          id="kenar-menu"
          ref={cekmeceRef}
          tabIndex={-1}
          aria-label="Ana menü"
          inert={!masaustu && !acik ? true : undefined}
          data-daraltilmis={daraltilmis ? "" : undefined}
          className={cn(
            "fixed inset-y-0 left-0 z-40 flex w-[260px] max-w-[85vw] shrink-0 flex-col",
            "text-menu-metin bg-(image:--gradyan-unk-menu)",
            /*
              BUTUN MENU KAYIYOR (profil blogu dahil) — eski panelde de oyle: menu ekrandan
              uzunsa menu kendi icinde kayar (tarama-1 §1.1).

              MENU SABIT (`fixed inset-y-0`), MASAUSTUNDE DE: onceden `md:sticky md:h-dvh` idi ve
              uzun sayfada mor zemin ekran yuksekliginde kesiliyordu (kanit: kanit-9a/B2/konu-takibi.png,
              ~990 px). `sticky` kendi kapsayicisinin (sayfa icerigi kadar yuksek flex satiri) icinde
              yasar; tam sayfa goruntulerde ve bazi kaydirma durumlarinda menu kapsayicinin ustune
              yapisip altini bos birakiyordu. `fixed` gorunum alanina bagli: yukseklik HER ZAMAN
              ekran kadar (`inset-y-0` — `100vh` degil, mobil tarayici cubugu yaniltmasin). Icerik
              sutunu menu genisligi kadar soldan bosluk birakiyor (asagida `md:pl-*`).
            */
            "yan-kaydirma overflow-y-auto",
            "ease-cikis transition-[translate,width] duration-(--sure-normal)",
            acik ? "translate-x-0" : "-translate-x-full",
            "md:max-w-none md:translate-x-0",
            daraltilmis ? "md:w-[83px]" : "md:w-[260px]",
          )}
        >
          {/* UST SATIR: hamburger + marka (62 px, altinda ince ayirici). Ad ORTAMDAN (marka.ts). */}
          <div
            className={cn(
              "border-menu-cizgi flex min-h-[3.875rem] shrink-0 items-center gap-3 border-b px-3",
              daraltilmis && "md:justify-center md:px-0",
            )}
          >
            <button
              type="button"
              onClick={hamburger}
              aria-controls="kenar-menu"
              aria-expanded={masaustu ? !daraltilmis : acik}
              className="size-dokunma bg-menu-aktif ring-menu-halka hover:bg-menu-uzeri inline-grid shrink-0 place-items-center rounded-[0.625rem] ring-1 transition-colors ring-inset"
            >
              <span className="sr-only">
                {!masaustu ? "Menüyü kapat" : daraltilmis ? "Menüyü genişlet" : "Menüyü daralt"}
              </span>
              <Ikon ad="menu" className="size-5" />
            </button>
            <span
              className={cn(
                "truncate text-[1.375rem] leading-none font-extrabold tracking-[0.02em] uppercase",
                daraltilmis && "md:hidden",
              )}
            >
              {marka.kisaAd}
            </span>
          </div>

          <ProfilBlogu profil={profil} daraltilmis={daraltilmis} />

          <ul className="flex flex-col gap-0.5 px-2.5 pt-3 pb-3">
            {duzen.ana.map((d) => {
              if (d.tur === "oge") return satir(d.oge, false);
              const acikMi = grupAcikMi(d.grup.anahtar);
              const icindeAktif = d.ogeler.some((o) => o.anahtar === aktif);
              const listeKimligi = `menu-grup-${d.grup.anahtar}`;
              return (
                <li key={`grup:${d.grup.anahtar}`}>
                  {/*
                    Grup basligi DUGME: acar/kapatir, `aria-expanded` durumu soyluyor. Baslik
                    vurgulanMIYOR (UNİKOÇ: "aktif alt oge vurgulanir, grup basligi vurgulanmaz");
                    yalniz ikon kipinde alt girdiler gizli oldugu icin aktif olani grup tasiyor.
                    `data-menu` BASLIGIN kabinda, li'de degil: tur soluklastirmasi opakligi
                    carpiyor ve li'ye verilseydi vurgulanan alt girdi de solgun kalirdi.
                  */}
                  <div data-menu={d.ogeler.flatMap((o) => o.ekranlar ?? [o.anahtar]).join(" ")}>
                    <MenuSatiri
                      ikon={d.grup.ikon}
                      etiket={d.grup.etiket}
                      alt={false}
                      secili={daraltilmis && icindeAktif}
                      daraltilmis={daraltilmis}
                      onClick={() => grubuDegistir(d.grup.anahtar)}
                      acilir={{ acik: acikMi && !daraltilmis, kontrol: listeKimligi }}
                    />
                  </div>
                  {acikMi && !daraltilmis && (
                    <ul id={listeKimligi} className="mt-0.5 flex flex-col gap-0.5">
                      {d.ogeler.map((o) => satir(o, true))}
                    </ul>
                  )}
                </li>
              );
            })}

            {/*
              ALT BOLUMLER — "ÖDÜL MERKEZİ": ustunde ince ayirici, kalin BUYUK HARF baslik
              (tiklanmaz), altinda girdiler. Baslik bir dugme degil, listeyi adlandiran
              etiket: `aria-labelledby` ekran okuyucuya "Odul Merkezi listesi" dedirtiyor.
            */}
            {duzen.bolumler.map(({ bolum, ogeler }) => {
              const kimlik = `menu-bolum-${bolum.anahtar}`;
              return (
                <li key={`bolum:${bolum.anahtar}`} className="border-menu-cizgi mt-2 border-t pt-2">
                  <p
                    id={kimlik}
                    className={cn(
                      "flex min-h-[2.875rem] items-center gap-2 pr-3.5 pl-[1.125rem] text-[0.9375rem] font-bold",
                      daraltilmis && "md:justify-center md:px-0",
                    )}
                  >
                    <Ikon ad={bolum.ikon} className="size-5" />
                    <span className={cn(daraltilmis && "md:sr-only")}>
                      {bolum.etiket.toLocaleUpperCase("tr-TR")}
                    </span>
                  </p>
                  <ul aria-labelledby={kimlik} className="flex flex-col gap-0.5">
                    {ogeler.map((o) => satir(o, false))}
                  </ul>
                </li>
              );
            })}

            {/* CIKIS YAP — EN ALTTA (karar 07.10.2026). Ayirici: hesaptan cikmak bir menu gezintisi degil. */}
            {onCikis && (
              <li className="border-menu-cizgi mt-2 border-t pt-2">
                <MenuSatiri
                  ikon="cikis"
                  etiket="Çıkış Yap"
                  alt={false}
                  secili={false}
                  daraltilmis={daraltilmis}
                  onClick={onCikis}
                />
              </li>
            )}
          </ul>
        </nav>

        {/* ICERIK SUTUNU. Zemin kok elemandan (UNİKOÇ lavanta→pembe degrade). */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/*
            UST SATIR — UNİKOÇ'ta serit YOK: yalniz sag ustte yuzen zil (tarama-1 §2.5).
            `<header>` kaliyor: axe "region" — sag ustteki icerik bir isaretli bolgede olmali (G2).
            Telefonda gizli: orada ayni isi mobil ust cubuk yapiyor.
          */}
          <header className="hidden min-h-16 items-center justify-end px-6 pt-4 md:flex">
            {saginUstu}
          </header>

          <main
            id="icerik"
            className="mx-auto w-full max-w-[1440px] min-w-0 flex-1 px-4 pt-4 pb-10 md:px-10 md:pt-2 md:pb-16"
          >
            {/*
              BASLIK BANDI KABUKTA — sayfanin TEK `h1`i. Aciklama ve eylemler ekranin
              `SayfaBasligiEki`siyle buraya portal ediliyor (yukaridaki gerekce).
            */}
            {baslikBandiGizli ? (
              <h1 className="sr-only">{baslik}</h1>
            ) : (
              <SayfaBasligi
                className="mb-6"
                baslik={baslik}
                ikon={baslikIkonu}
                /* ODUL MERKEZI ekranlari (Magaza, Bonus Carki...): degrade karo + beyaz ikon (tarama-2 §2.1). */
                ikonTuru={menu.find((o) => o.anahtar === aktif)?.bolum ? "degrade" : "beyaz"}
                aciklama={
                  <>
                    <span ref={setAciklamaYuvasi} />
                    {ogrenciAdi && (
                      <span className="bg-notr-0 text-mor-700 border-mor-kenar ml-2 inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 align-middle text-xs font-bold">
                        <Ikon ad="ogrenci" boyut="kucuk" />
                        <span className="sr-only">Öğrenci: </span>
                        {ogrenciAdi}
                      </span>
                    )}
                  </>
                }
                eylemler={<span ref={setEylemYuvasi} className="contents" />}
              />
            )}
            {altSekmeler}
            <BaslikYuvasiBaglami.Provider
              value={{ aciklama: aciklamaYuvasi, eylemler: eylemYuvasi }}
            >
              {children}
            </BaslikYuvasiBaglami.Provider>
          </main>
        </div>
      </div>
    </div>
  );
}

/**
 * Yan menudeki tek satir — tek girdi, alt girdi, grup basligi ve Cikis Yap
 * AYNI gorunumu kullaniyor (UNİKOÇ: "ust seviye ve alt oge ayni kalip").
 *
 *   secili   yari saydam beyaz hap, 14 px kose, 1 px acik kenar; yazi KALINLASMIYOR
 *   alt      ~37 px iceride, 14 px yazi, 18 px ikon
 *   ust      15 px yazi, 20 px ikon; uzun etiket iki satira sarar (kesilmez)
 *
 * YUKSEKLIK: ust satir 46 px, alt satir 44 px — ikisi de 44 px dokunma hedefinin ustunde.
 */
function MenuSatiri({
  ikon,
  etiket,
  alt,
  secili,
  daraltilmis,
  onClick,
  acilir,
}: {
  ikon: IkonAdi;
  etiket: string;
  alt: boolean;
  secili: boolean;
  daraltilmis: boolean;
  onClick: () => void;
  acilir?: { acik: boolean; kontrol: string };
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={secili && !acilir ? "page" : undefined}
      aria-expanded={acilir ? acilir.acik : undefined}
      aria-controls={acilir?.acik ? acilir.kontrol : undefined}
      title={daraltilmis ? etiket : undefined}
      className={cn(
        "text-menu-metin flex items-center rounded-[0.875rem] text-left leading-snug",
        "ease-cikis transition-colors duration-(--sure-anlik)",
        /*
          OLCULER (Curriculum-Index, 1:1): ust satir ikonu menu kenarindan 28 px, metin 56 px;
          alt satirin HAPI 46 px iceriden baslar (vurgu girintili, tam genislik degil), ikonu 65 px.
        */
        alt
          ? "min-h-dokunma ml-9 w-[calc(100%-2.25rem)] gap-1.5 py-1.5 pr-3 pl-[1.125rem] text-[0.875rem]"
          : "min-h-[2.875rem] w-full gap-2 py-1.5 pr-3.5 pl-[1.125rem] text-[0.9375rem]",
        daraltilmis && "md:justify-center md:px-0",
        secili ? "bg-menu-aktif ring-menu-halka ring-1 ring-inset" : "hover:bg-menu-uzeri",
      )}
    >
      <Ikon ad={ikon} className={alt ? "size-[1.125rem]" : "size-5"} />
      <span className={cn("min-w-0 flex-1", daraltilmis && "md:sr-only")}>{etiket}</span>
      {acilir && (
        <Ikon ad="asagiOk" className={cn("size-[1.125rem]", daraltilmis && "md:hidden")} />
      )}
    </button>
  );
}

/**
 * PROFIL BLOGU — ortali yuvarlak avatar, ad, rol (tarama-1 §1.1). Fotograf
 * altyapisi personelde YOK; avatar bas harflerden. Daraltilinca yalniz kucuk
 * avatar kaliyor. Profil yuklenmeden (`undefined`) yer tutucu cember: blok
 * yuklenince menu asagi ziplamasin.
 */
function ProfilBlogu({
  profil,
  daraltilmis,
}: {
  profil: KabukProfili | null | undefined;
  daraltilmis: boolean;
}) {
  return (
    <div
      className={cn(
        "border-menu-cizgi flex shrink-0 flex-col items-center border-b px-3 pt-5 pb-4 text-center",
        daraltilmis && "md:px-0 md:pt-3 md:pb-3",
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "bg-menu-aktif ring-notr-0/85 grid size-[5.5rem] place-items-center rounded-full font-bold ring-[3px]",
          "text-2xl",
          daraltilmis && "md:size-[2.875rem] md:text-sm md:ring-2",
        )}
      >
        {profil ? basHarfler(profil.ad) : <Ikon ad="profil" className="size-8" />}
      </span>
      {profil && (
        <div className={cn("mt-3 w-full", daraltilmis && "md:sr-only")}>
          <p className="truncate text-[0.9375rem]">{profil.ad}</p>
          {profil.rol && (
            <p className="mt-1 truncate text-xs font-bold tracking-[0.04em]">
              {profil.rol.toLocaleUpperCase("tr-TR")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** `md` kirilimi (768 px) — Tailwind ile ayni esik; cekmece yalnizca altinda cekmece. */
const MASAUSTU_SORGUSU = "(min-width: 768px)";

function useMasaustu(): boolean {
  const sorgu = (): boolean =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(MASAUSTU_SORGUSU).matches
      : true;
  const [masaustu, setMasaustu] = useState(sorgu);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(MASAUSTU_SORGUSU);
    const degisti = (): void => setMasaustu(mq.matches);
    mq.addEventListener("change", degisti);
    return () => mq.removeEventListener("change", degisti);
  }, []);
  return masaustu;
}
